import './main.component.css';
import { Component, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';

import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { SdmUtilService } from '../../services/sdm-util.service';
import { ContentService } from '../../services/content.service';
import { Auth0Service } from '../../services/auth0.service';
import { Auth0LockService } from '../../services/auth0-lock.service';
import { SHARED_IMPORTS } from '../shared/shared-imports';


@Component({
	standalone: true,
	selector: 'main-component',
	moduleId: module.id,
	templateUrl: 'components/main/main.component.html',
	styleUrls: ['components/main/main.component.css'],
	imports: [
		...SHARED_IMPORTS,
		MatCardModule,
		MatIconModule
	]
})
export class MainComponent implements OnInit {
	private content = inject(ContentService); 
	private http = inject(HttpClient);
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private auth0Service = inject(Auth0Service);
	private auth0LockService = inject(Auth0LockService);
	private sdmUtil = inject(SdmUtilService);
	data: any;
	LastCriticalAlarm: any;
	ready = false;
	withNavbar = true;

	
	constructor(
	) { }

	ngOnInit(): void {
		// Handle login routes - trigger Auth0 authentication
		const currentPath = this.router.url;
		const isLoginRoute = currentPath === '/Login' || currentPath === '/LoginGlobal';
		const isLogoutRoute = currentPath === '/Logout';
		
		if (isLoginRoute) {
			this.handleLoginRoute(currentPath);
			return; // Don't load main content for login routes
		}

		if (isLogoutRoute) {
			this.handleLogoutRoute();
			return;
		}

		// Regular main component initialization
		this.initializeMainComponent();
	}

	private initializeMainComponent(): void {
		this.getLastCriticalAlarm();
		this.content.initAll().then(() => {
			this.http.get('/api/Main/MainOverview').subscribe((response: any) => {
				this.data = response;
				this.ready = true;
			}, (error) => {
				console.error('MainComponent: API call failed:', error);
				// Set ready to true anyway so we can see the component
				this.ready = true;
			});
		}).catch(err => {
			console.error('MainComponent: Content initialization failed:', err);
			// Set ready to true anyway so we can see the component
			this.ready = true;
		});
	}

	getAlarmClass(): string {
		if (!this.data) return '';
		return this.data.AlarmsCriticalCount > 0
			? 'critical'
			: this.data.AlarmsNoncriticalCount > 0
				? 'noncritical'
				: '';
	}

	hasAlarms(): boolean {
		return this.data
			&& (parseInt(this.data.AlarmsCriticalCount) + parseInt(this.data.AlarmsNoncriticalCount)) > 0;
	}

	hasCriticalAlarms(): string {
		return this.data?.AlarmsCriticalCount > 0 ? 'boldFont' : '';
	}

	getLastCriticalAlarm(): void {
		if (!this.LastCriticalAlarm) {
			this.http.get('/api/Alarm/LastCriticalAlarm').subscribe((response: any) => {
				this.LastCriticalAlarm = response?.[0] || '';
			});
		}
	}

	getErrorPastTime(error: any): string {
		return this.sdmUtil.getErrorPastTime(error, this.content);
	}

	gotoAlarms(): void {
		this.router.navigate(['/AlarmOverview']);
	}

	gotoLocations(): void {
		this.router.navigate(['/LocationOverview']);
	}

	gotoUnits(): void {
		this.router.navigate(['/UnitOverview']);
	}

	private handleLoginRoute(currentPath: string): void {
		// Check if we're returning from Auth0 Lock with tokens (callback handling is done by Auth0LockService)
		if (window.location.href.indexOf('access_token=') !== -1 || 
			window.location.href.indexOf('id_token=') !== -1) {
			// Auth0 Lock handles the callback automatically, just return
			return;
		}

		// Show Auth0 Lock modal based on route
		this.showLoginModal(currentPath);
	}

	private showLoginModal(currentPath: string): void {
		const requestedUrl = this.route.snapshot.data['requestedUrlBeforeLogin'] ||
						   (currentPath === '/LoginGlobal' ? 'GlobalAdmin' : 'Main');
		if (currentPath === '/LoginGlobal') {
			this.auth0LockService.showGlobalLogin(requestedUrl);
		} else {
			this.auth0LockService.showLogin(requestedUrl);
		}
	}

	private handleLogoutRoute(): void {
		// Auth0 Lock service handles clearing tokens and logout
		this.auth0LockService.logout();
	}
}
